import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "317",
  "slug": "how-many-oceans-are-there",
  "title": "How Many Oceans Are There? The Five-Ocean Debate, Settled",
  "excerpt": "Some textbooks list four oceans, others five, and a few persist with seven. So how many oceans are there really, and which answer is the right one in 2026? Here's a clear explainer of the debate, the geography, and what changed in 2021.",
  "tldr": "There are five oceans: the Pacific, Atlantic, Indian, Arctic, and Southern. The Southern Ocean was formally recognised by the International Hydrographic Organization in 2000 and by the National Geographic Society in 2021. Older textbooks list four because they treat the waters around Antarctica as the southern reaches of the other three.",
  "date": "2026-04-27",
  "category": "Geography",
  "image": "/blog-images/world.jpg",
  "readTime": "6 min",
  "content": [
    {
      "type": "paragraph",
      "text": "If you went to school in different decades, you may have learned a different number. Children educated in the mid-20th century were often taught there are seven seas. Children educated in the 1980s and 1990s were usually told there are four oceans. Children educated since 2000 are increasingly being told there are five. So which is right?"
    },
    {
      "type": "paragraph",
      "text": "The honest answer is that all of the world's oceans are one continuous body of saltwater, and the divisions between them are conventions chosen for geographic, oceanographic, and political convenience. But within those conventions, there is now a strong and growing consensus on five distinct oceans — and the change to five became official in 2021. Here is the breakdown."
    },
    {
      "type": "heading",
      "text": "The Five Oceans (the Current Consensus)"
    },
    {
      "type": "paragraph",
      "text": "Since 2021, the National Geographic Society — one of the most influential cartographic authorities in the world — has officially recognised a fifth ocean: the Southern Ocean. This brought their classification in line with the U.S. Board on Geographic Names, the International Hydrographic Organization (since 2000), and a growing body of oceanographers who had argued for the change for decades. The current five-ocean model is:"
    },
    {
      "type": "list",
      "items": [
        "The Pacific Ocean — the largest ocean by far, covering about 165 million km² and stretching from Asia and Australia to the Americas. It contains over half of the world's free-flowing water and reaches the deepest point on Earth (the Challenger Deep in the Mariana Trench at roughly 10,935 metres below sea level).",
        "The Atlantic Ocean — the second-largest, at about 106 million km², running north to south between the Americas and Europe/Africa.",
        "The Indian Ocean — the third-largest, at about 70 million km², bounded by Africa, Asia, and Australia. It is the warmest of the major oceans.",
        "The Southern Ocean — about 20 million km², surrounding Antarctica from roughly 60°S to the continent. Made official in 2000 by the IHO and recognised by National Geographic in 2021.",
        "The Arctic Ocean — the smallest at about 14 million km², centred on the North Pole and bounded by Eurasia and North America. Most of it is covered by sea ice that fluctuates dramatically with the seasons."
      ]
    },
    {
      "type": "heading",
      "text": "Why the Southern Ocean Got Promoted"
    },
    {
      "type": "paragraph",
      "text": "The case for the Southern Ocean is oceanographic, not just geographic. The waters around Antarctica behave fundamentally differently from those further north. They are colder, denser, and more saline. They are bounded by the Antarctic Circumpolar Current — the strongest ocean current on Earth — which encircles the continent and isolates Antarctic waters from those further north. The Antarctic Circumpolar Current carries about 100 times more water than all the world's rivers combined, and it has been flowing continuously for around 34 million years since the gap between Antarctica and South America (the Drake Passage) opened up."
    },
    {
      "type": "paragraph",
      "text": "Functionally, the Southern Ocean is its own system. It plays a uniquely large role in regulating global climate, drives much of the deep ocean circulation that distributes heat around the planet, and absorbs a disproportionate share of the world's atmospheric carbon. Treating it as a separate ocean reflects the oceanography rather than just dividing the South Atlantic, South Pacific, and South Indian into latitude bands."
    },
    {
      "type": "heading",
      "text": "Why Some Textbooks Still Say Four"
    },
    {
      "type": "paragraph",
      "text": "The four-ocean model — Pacific, Atlantic, Indian, Arctic — was the standard taught for most of the 20th century. Many textbooks have not yet been updated, especially outside the United States, and the four-ocean classification still appears in many education systems. This is not technically wrong — the IHO did not formally adopt the five-ocean model until 2000, and the Southern Ocean's boundaries are still being negotiated diplomatically. But the scientific consensus now favours five, and most major reference works (National Geographic, NOAA, the U.S. Department of State) follow suit."
    },
    {
      "type": "heading",
      "text": "And the Seven Seas?"
    },
    {
      "type": "paragraph",
      "text": "The phrase \"the seven seas\" is much older than modern oceanography. Different cultures have used it to mean different things — medieval Arab navigators counted seven specific bodies of water connecting the Arabian peninsula to East Africa and China, the Greeks and Romans used it to mean the seas of the Mediterranean and surrounding regions, and the modern English idiom is a poetic catch-all for \"all the world's seas.\" When applied to the modern world, one common reconstruction is:"
    },
    {
      "type": "list",
      "items": [
        "North Pacific",
        "South Pacific",
        "North Atlantic",
        "South Atlantic",
        "Indian",
        "Arctic",
        "Southern (Antarctic)"
      ]
    },
    {
      "type": "paragraph",
      "text": "This is sometimes called the \"seven oceans\" model and is essentially the modern five oceans with the Pacific and Atlantic each subdivided north and south. It is not the standard scientific or cartographic convention today, but it is sometimes still taught — particularly in countries that have older curriculums or that historically used the seven-seas terminology."
    },
    {
      "type": "heading",
      "text": "What About Seas?"
    },
    {
      "type": "paragraph",
      "text": "Oceans are the largest divisions of the world's saltwater. Within them, dozens of smaller bodies of water are called seas — partially enclosed by land, or distinguished by oceanographic features. The Mediterranean Sea, the Caribbean Sea, the South China Sea, the Coral Sea, the Bering Sea, the Sea of Japan, and many others are subdivisions of the larger oceans. The exact list of seas is itself debated and varies by source. The IHO recognises around 50 named seas; some references list over 100."
    },
    {
      "type": "paragraph",
      "text": "Practically, when people say \"the seven seas\" today, they almost always mean it idiomatically rather than as a precise count. When people ask how many oceans there are, the right modern answer is five."
    },
    {
      "type": "heading",
      "text": "Why This Matters"
    },
    {
      "type": "paragraph",
      "text": "The question \"how many oceans are there?\" is one of those geography questions that sounds trivial but is actually a window into how scientific consensus shifts. The promotion of the Southern Ocean to a fifth ocean reflects decades of oceanographic research, a growing awareness of Antarctica's central role in regulating the Earth's climate, and a willingness to update conventions when the science demands it. It is also a useful reminder that geographic names and boundaries are choices, not facts of nature — choices that get revised when our understanding of the planet improves."
    },
    {
      "type": "paragraph",
      "text": "Next time someone asks you how many oceans there are, the answer is five. And if anyone tries to tell you it's only four, you can tell them their map is 25 years out of date."
    }
  ],
  "imageAlt": "Earth regional satellite composite with shaded relief",
  "imageCaption": "NASA Earth Observatory / MODIS, August 2004. Regional composite with shaded relief and bathymetry; it shows landforms, not political boundaries or current conditions.",
  "sources": [
    {
      "title": "NASA: Blue Marble Next Generation (image source)",
      "url": "https://science.nasa.gov/earth/earth-observatory/blue-marble-next-generation/"
    }
  ]
};
